/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { DEFAULT_PROPERTY_JOBS_SECTION_STYLES, type PropertyJobsSectionStyles } from './propertyJobsSectionStyles';

/** The activity table reads like the jobs table above it, so it starts from the same styles. */
export type PropertyActivitySectionStyles = PropertyJobsSectionStyles & {
  filter: SerializedStyles;
  /** What happened: set in from the Type column, as PropertyOverview.html's .po-tl-what is. */
  whatCell: SerializedStyles;
  /** The connector's cell: a line through every row, so the feed reads as one thread. */
  railCell: SerializedStyles;
  /** The first row's connector starts at its own mark rather than the top of the row. */
  firstRailCell: SerializedStyles;
  marker: SerializedStyles;
  /* The marks mean what they mean on a job's activity: a thing started is solid ink, a thing
     finished is the only green, and archiving - nothing done, only put away - is an outline. */
  markerByEvent: Partial<Record<'projectCompleted' | 'projectArchived', SerializedStyles>>;
  more: SerializedStyles;
};

export const DEFAULT_PROPERTY_ACTIVITY_SECTION_STYLES: PropertyActivitySectionStyles = {
  ...DEFAULT_PROPERTY_JOBS_SECTION_STYLES,
  /*
   * The design's Activity grid (PropertyOverview.html .po-tl): the date, the rail, Who, Job, Type,
   * What. Its first five tracks are the Jobs table's, so the two tables' columns line up. (The
   * design's last track, the money, is the job-total ticket's.)
   */
  table: css`
    label: PROPERTY_ACTIVITY_TABLE;
    display: grid;
    grid-template-columns: 88px 18px 104px 26% 11% minmax(110px, 1fr);
    width: 100%;
    min-width: 860px;

    & thead,
    & tbody,
    & tr {
      display: contents;
    }
  `,
  row: css`
    label: PROPERTY_ACTIVITY_ROW;
    & + & > td {
      border-top: 1px solid ${COLORS.stroke};
    }
  `,
  /* Everything on a row's first line, so the mark sits level with the date. */
  cell: css`
    ${DEFAULT_PROPERTY_JOBS_SECTION_STYLES.cell};
    label: PROPERTY_ACTIVITY_CELL;
    align-items: flex-start;
  `,
  /* The date stacks against the rail, as in PropertyOverview.html's .po-tl-when. */
  dateCell: css`
    label: PROPERTY_ACTIVITY_DATE_CELL;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    white-space: nowrap;
    text-align: right;
    padding-left: 0;
    padding-right: 14px;
  `,
  whatCell: css`
    label: PROPERTY_ACTIVITY_WHAT_CELL;
    padding-left: 64px;
  `,
  filter: css`
    label: PROPERTY_ACTIVITY_FILTER;
    margin-left: auto;
    padding: 6px 8px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    font-family: ${FONT.body};
    font-size: ${pxToRem(12.5)};
    color: ${COLORS.ink};
  `,
  railCell: css`
    label: PROPERTY_ACTIVITY_RAIL_CELL;
    position: relative;
    width: 18px;
    padding: 0;
    text-align: center;

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 1px;
      background: ${COLORS.stroke};
      transform: translateX(-50%);
    }
  `,
  firstRailCell: css`
    label: PROPERTY_ACTIVITY_RAIL_CELL_FIRST;
    &::before {
      top: 17px;
    }
  `,
  /* An 8px square turned to a diamond, on the connector, level with the date's first line. */
  marker: css`
    label: PROPERTY_ACTIVITY_MARKER;
    position: relative;
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-top: 16px;
    background: ${COLORS.ink};
    transform: rotate(45deg);
  `,
  markerByEvent: {
    projectCompleted: css`
      label: PROPERTY_ACTIVITY_MARKER_COMPLETED;
      background: ${COLORS.success};
    `,
    projectArchived: css`
      label: PROPERTY_ACTIVITY_MARKER_ARCHIVED;
      background: ${COLORS.white};
      box-shadow: inset 0 0 0 1.5px ${COLORS.ink};
    `,
  },
  more: css`
    label: PROPERTY_ACTIVITY_MORE;
    margin-top: 10px;
    border: 0;
    padding: 0;
    background: none;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9.5)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${COLORS.ink};
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;

    &:disabled {
      color: ${COLORS.textTertiary};
      cursor: default;
    }
  `,
};
